import { FormControl, FormGroup } from '@angular/forms';

export interface Driver {
  alerting_email: string;
  creation_date: string;
  driver_has_subscription: boolean;
  email: string;
  first_name: string;
  last_name: string;
  locale: string;
  pending: boolean;
  user_id: string;
  bank_information_available: boolean;
  iban: string;
  bic: string;
  bank_account_holder: string;
  onboarding_status:
    'invited' | 'registered' | 'wallbox_selected' | 'order_placed' | 'installed' | 'completed';
}

export interface DriverExtended extends Driver {
  wbEndpointId?: string;
  wbUUID?: string;
}

export interface DriverTab {
  id: string;
  nameTrx: string;
}

export interface ChargePoint {
  active_tariff_meter: string;
  alias_endpoint_id: string;
  charge_point_status: ChargePointStatus[];
  company_id_tag: string;
  default_car_id: string;
  direct_ocpp: boolean;
  driver_id: string;
  /** Nested driver object; can be null for unassigned / incomplete charge points. */
  driver: Driver | null;
  driver_has_subscription: boolean;
  eichrecht_charging: boolean;
  endpoint_id: string;
  is_charging: boolean;
  organization_id: string;
  prices: Price[];
  provider_id_tag: string;
  provider_registered: boolean;
  users: User[];
  uuid: string;
}

export interface ChargePointOnlineStatus {
  online_status: true;
}

export interface ChargePointStatus {
  connector_id: number;
  info: string;
  status: string;
  status_time: string;
  uuid: string;
  vendor_error_code: string;
  vendor_id: string;
}

export interface Price {
  contract_available: boolean;
  contract_timestamp: string;
  id: number;
  last_verification_timestamp: string;
  price: number;
  price_verified: boolean;
  timestamp: string;
  uuid: string;
}

export interface User {
  alerting_email: string;
  creation_date: string;
  driver_has_subscription: boolean;
  email: string;
  first_name: string;
  last_name: string;
  locale: string;
  pending: boolean;
  user_id: string;
}

export interface DriverKey {
  key: string;
  value: string;
}

export interface DriverKeyFull extends DriverKey {
  information_id: string;
  organization_id: string;
  user_id: string;
}

export interface DriverInvitationForm {
  email: FormControl<string>;
  language: FormControl<string>;
  ediControls: FormGroup;
}

export interface EdiKey {
  key: string;
  regular_expression: string;
  user_writable: boolean;
}

export type PricePolicyType = 'individual' | 'flatrate' | 'selective';

export function toPricePolicyType(value: string | null | undefined): PricePolicyType | null {
  const type = value?.toLowerCase();
  if (type === 'individual' || type === 'flatrate' || type === 'selective') {
    return type;
  }
  return null;
}

export interface CreatePricePolicyRequest {
  inform_drivers_by_email: boolean;
  price_policy: PricePolicyType;
  year: number;
}

export interface OrganizationPricePolicy {
  organization_id: string;
  organization_price_policy_id: string;
  price_policy: PricePolicyType;
  year: number;
}

export interface OrganizationPricePolicies {
  active: OrganizationPricePolicy;
  upcoming: OrganizationPricePolicy | null;
}

export interface FlatratePrice {
  active: boolean;
  global_flatrate_price_id: string;
  value: number;
  year: number;
}

export interface DriverOrganization {
  automatic_export_activated: boolean;
  automatic_export_day_of_month: number;
  automatic_export_emails: string[];
  automatic_export_locale: string;
  charge_assign_email_time_seconds: number;
  charge_assign_time_seconds: number;
  eichrecht_charging: boolean;
  eichrecht_reset_after_charge: boolean;
  global_price: number;
  organization_id: string;
  organization_name: string;
  price_policy: OrganizationPricePolicies;
  pricing_model: 'individual_price' | string;
  provider_id: string;
  provider_type: string;
  reimbursement_activated: boolean;
  reimbursement_bic: string;
  reimbursement_iban: string;
  backend_provider_driver_idtag: boolean;
  direct_ocpp_idtag_assign_charges: boolean;
  charge_assign_allow_automatic_company_car: boolean;
  mileage_request_activated: boolean;
  mileage_email_after_days: number;
  mileage_outdated_days: number;
}

export interface DriverInvitePayload {
  driver_edis: { key: string; value: string }[];
  locale: string;
  user_email: string;
}

export interface DriverInvitation {
  user_email: string;
  locale: string;
  driver_edi_list: { key: string; value: string }[];
}

export interface AutomaticExportRequest {
  automatic_export_day_of_month: number;
  automatic_export_locale: string;
}

export interface AddExportReceiverRequest {
  automatic_export_emails: string[];
}

export interface AutomaticExportForm {
  language: FormControl<string>;
  dayOfMonth: FormControl<number>;
}

export interface ChargesForm {
  emailReminderTime: FormControl<number>;
  assignmentTime: FormControl<number>;
}

export interface UpdateChargeAssignSettingsRequest {
  charge_assign_email_time_seconds: number;
  charge_assign_time_seconds: number;
}

export interface MileageSettingsForm {
  emailAfterDays: FormControl<number>;
  outdatedDays: FormControl<number>;
}

export interface UpdateMileageSettingsRequest {
  mileage_email_after_days: number;
  mileage_outdated_days: number;
}

export interface BackendProvider {
  provider_id: string;
  provider_name: string;
  provider_type: string;
  provider_url: string;
}

export interface ChargingStationBackendData {
  alias_endpoint_id: string;
  backend_provider_id: string;
  backend_provider_password: string;
  backend_provider_username: string;
  connection_type: string;
  device_cert: string;
  device_cert_key: string;
  endpoint_id: string;
  provider_id_tag: string;
  uuid: string;
}

export interface ChargingStationBackendDataRequest {
  alias_endpoint_id?: string;
  backend_provider_username?: string;
  backend_provider_password?: string;
  provider_id_tag?: string;
}

export interface ChargingEvent {
  uuid: string;
  transaction_start: string;
  transaction_stop: string;
  driver_id: string;
  charge_cost: number;
  consumption: number;
  driver: string;
  provider_transaction_id: number;
  provider_transaction_start_successful: boolean;
  provider_transaction_stop_successful: boolean;
  repay_state: 'OPEN' | 'OPEN_LATE' | 'DATA_EXPORTED';
  transaction_id: number;
}

export interface Mileage {
  total_kilometers: number;
  timestamp: string;
}

export interface LatestPriceResponse {
  latest_price: Price;
  latest_upcoming_price: Price;
}

export interface ExportDocument {
  creation_date: string;
  export_file_type: string;
  export_id: string;
  export_target_month: string;
  exporting_manager: string;
  exporting_manager_email: string;
  organization_id: string;
}

export interface Export {
  creation_date: string;
  export_documents: ExportDocument[];
  export_end_date: string;
  export_id: string;
  export_start_date: string;
  export_target_month: string;
  exporting_manager: string;
  exporting_manager_email: string;
  organization_id: string;
}

export type ReimbursementState =
  'open' | 'in-progress' | 'waiting-for-payment' | 'in-payout' | 'completed' | 'error';

export interface Reimbursement {
  created_date: string;
  error_reason: string;
  export_id: string;
  organization_id: string;
  reimbursement_id: string;
  reimbursement_state: ReimbursementState | string;
  ta_code: string;
  user_id: string;
}

export interface ChargePointMetadata {
  device_type: string;
  firmware_version: string;
  location: { lat: number; lng: number };
  name: string;
  online_status: boolean;
  online_status_timestamp: string;
  tags: string[];
}

export interface ChargePointDetail extends ChargePoint {
  metadata: ChargePointMetadata | null;
}
